import { useCallback, useEffect, useRef, useState } from 'react';
import I18n from 'I18n';
import hubHttpClient from 'hub-http/clients/apiClient';
import formatRelativeDuration from 'I18n/utils/formatRelativeDuration';
import { callIfPossible } from 'UIComponents/core/Functions';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { FormActions } from 'foundations-components/transitional/components/FormActions';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Button } from 'foundations-components/transitional/components/Button';
import { fetchMeetings } from './fetchMeetings';
import { MEETINGS_LINK_TYPES as LINK_TYPES, MEETING_LINK_DOMAINS, SENDER_MEETINGS_LINK_TOKEN } from 'hubspot-prosemirror/draft-transplant/utils/constants';
import getDefaultMeetingsLink from './getDefaultMeetingsLink';
import MeetingPopoverEmptyState from './MeetingPopoverEmptyState';
import FormattedMessage from 'I18n/components/FormattedMessage';
import MeetingsLinkSelection from './MeetingsLinkSelection';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getReadableDurations(durations) {
  const durationList = I18n.formatList(durations.sort((a, b) => b - a).map(duration => formatRelativeDuration(I18n.moment.duration(duration))));
  return I18n.text('draftPlugins.meetings.meetingDuration', {
    durationList
  });
}
function getMeetingsLinkOptions(meetingsLinks) {
  return meetingsLinks.map(link => {
    const isDefaultLink = link.isDefaultLink;
    const durations = link.customParams.durations;
    const name = link.name || getReadableDurations(durations);
    const _linkURL = link.link;
    const dropdownText = isDefaultLink ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "hubspot-prosemirror.meetings.default",
      options: {
        name
      }
    }) : name;
    return {
      text: name,
      dropdownText,
      value: _linkURL,
      help: _linkURL
    };
  });
}
function updateMeetingsLinkDomain(meetingsLinks, originalLinkURL) {
  if (!originalLinkURL) {
    return null;
  }
  const linkSlug = MEETING_LINK_DOMAINS.reduce((fullUrl, domain) => fullUrl.indexOf(domain) !== -1 ? fullUrl.replace(domain, '') : fullUrl, originalLinkURL);
  const currentLink = meetingsLinks.find(link => link.slug === linkSlug || link.link === originalLinkURL);
  if (currentLink && currentLink.link !== originalLinkURL) {
    return currentLink.link;
  }
  return null;
}
const MeetingsPopover = props => {
  const {
    showHeader,
    isNew,
    allowSenderMeetingLink = false,
    onClose,
    initialMeetingName = '',
    initialMeetingLinkType = LINK_TYPES.CUSTOM,
    initialMeetingURL = null,
    getMeetingsLink = fetchMeetings,
    onFetchMeetingsError,
    onChange,
    apiClient = hubHttpClient
  } = props;
  const [hasFetchedMeetings, setHasFetchedMeetings] = useState(false);
  const [meetingsLinks, setMeetingsLinks] = useState([]);
  const [meetingsLinkOptions, setMeetingsLinkOptions] = useState([]);
  const [linkType, setLinkType] = useState(initialMeetingLinkType);
  const [linkURL, setLinkURL] = useState(initialMeetingURL);
  const [linkName, setLinkName] = useState(null);
  const [text, setText] = useState(initialMeetingName);
  const inputRef = useRef(null);
  const handleTextChange = useCallback(({
    target
  }) => {
    const {
      value
    } = target;
    setText(value);
  }, []);
  const getSelectedMeetingName = useCallback(() => {
    if (linkType === LINK_TYPES.SENDER) {
      return SENDER_MEETINGS_LINK_TOKEN;
    }
    const defaultMeeting = getDefaultMeetingsLink(meetingsLinks);
    const durations = defaultMeeting.customParams.durations;
    return linkName || defaultMeeting.name || getReadableDurations(durations);
  }, [linkName, linkType, meetingsLinks]);
  const getSelectedMeetingURL = useCallback(() => {
    if (linkType === LINK_TYPES.SENDER) {
      return `{{ custom.${LINK_TYPES.SENDER} }}`;
    }
    const defaultMeeting = getDefaultMeetingsLink(meetingsLinks);
    return linkURL || defaultMeeting.link;
  }, [linkType, linkURL, meetingsLinks]);
  const handleClose = useCallback(() => {
    callIfPossible(onClose);
  }, [onClose]);
  const handleConfirm = useCallback((closePopover = true) => {
    const meetingURL = getSelectedMeetingURL();
    const meetingName = getSelectedMeetingName();
    const textState = text || meetingName || '';
    onChange({
      meetingURL,
      meetingName: textState
    });
    if (closePopover) {
      handleClose();
    }
  }, [getSelectedMeetingName, getSelectedMeetingURL, handleClose, onChange, text]);
  useEffect(() => {
    getMeetingsLink({
      httpClient: apiClient
    }).then(meetingsLinksResponse => {
      const updatedLinkURL = updateMeetingsLinkDomain(meetingsLinksResponse, linkURL);
      setMeetingsLinks(meetingsLinksResponse);
      setMeetingsLinkOptions(getMeetingsLinkOptions(meetingsLinksResponse));
      setLinkURL(() => {
        if (updatedLinkURL) {
          handleConfirm(false);
        }
        return updatedLinkURL || linkURL;
      });
    }).catch(err => {
      callIfPossible(onFetchMeetingsError, err);
    }).finally(() => {
      setHasFetchedMeetings(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // Focus on the input after the meetings have been fetched
    // This useEffect should only run once as the hasFetchedMeetings flag is set to true only after the getMeetingsLink promise resolves
    if (hasFetchedMeetings && inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }, [hasFetchedMeetings]);
  if (hasFetchedMeetings && meetingsLinks.length === 0) {
    return /*#__PURE__*/_jsx(MeetingPopoverEmptyState, {});
  }
  if (!hasFetchedMeetings) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      minHeight: 350
    });
  }
  const confirmText = isNew ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.meetings.confirm"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.meetings.confirmUpdate"
  });
  const title = isNew ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.meetings.popoverTitle"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.meetings.popoverTitleEdit"
  });
  return /*#__PURE__*/_jsxs(View, {
    padding: "400",
    inlineSize: "400px",
    "data-test-id": "meetings-popover",
    children: [showHeader && /*#__PURE__*/_jsx("h5", {
      children: title
    }), /*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.meetings.textLabel"
      }),
      children: /*#__PURE__*/_jsx(TextInput, {
        onChange: handleTextChange,
        value: text,
        placeholder: I18n.text('hubspot-prosemirror.meetings.textPlaceholder'),
        inputRef: inputRef
      })
    }), /*#__PURE__*/_jsx(MeetingsLinkSelection, {
      meetingsLinks: meetingsLinks,
      linkURL: linkURL,
      meetingsLinkOptions: meetingsLinkOptions,
      text: text,
      linkName: linkName,
      linkType: linkType,
      setText: setText,
      setLinkURL: setLinkURL,
      setLinkName: setLinkName,
      setLinkType: setLinkType,
      allowSenderMeetingLink: allowSenderMeetingLink
    }), /*#__PURE__*/_jsxs(FormActions, {
      marginBlockStart: "500",
      marginBlockEnd: "0",
      children: [/*#__PURE__*/_jsx(Button, {
        "data-test-id": "apply-meeting-link",
        use: "tertiary",
        size: "small",
        onClick: () => handleConfirm(),
        children: confirmText
      }), /*#__PURE__*/_jsx(Button, {
        use: "tertiary-light",
        size: "small",
        onClick: handleClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "hubspot-prosemirror.meetings.cancel"
        })
      })]
    })]
  });
};
export default MeetingsPopover;